import Image from 'next/image';
import { FunctionComponent, useCallback, useEffect, useState, useMemo } from 'react';

import useIsMounted from '@/hooks/useIsMounted';
import DataLayer from '@/utils/DataLayer';

interface ThankYouProps extends RewindSlideProps {
  shareImageUrl: string;
}

const ThankYou: FunctionComponent<ThankYouProps> = (props) => {
  const { shareImageUrl, active = true, sdkReady = false } = props;
  const [imageBlob, setImageBlob] = useState<Blob>();
  const isMounted = useIsMounted();

  const [showShareButton, setShowShareButton] = useState(false);
  const imageDataUrl = useMemo(() => {
    if (imageBlob) {
      return URL.createObjectURL(imageBlob);
    }

    return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
  }, [imageBlob]);

  useEffect(() => {
    if (sdkReady && isMounted()) {
      fetch(shareImageUrl)
        .then(async (image) => {
          setImageBlob(await image.blob());
        });
    }
  }, [sdkReady, isMounted, shareImageUrl]);

  useEffect(() => {
    // @ts-ignore - navigator share may not be supported in some browsers
    if (navigator.share && navigator.canShare && imageBlob) {
      const file = new File([imageBlob], 'my-sbs-rewind-2023.png', { type: 'image/png' });

      if (navigator.canShare({ files: [file] })) {
        setShowShareButton(true);
      }
    }
  }, [imageBlob]);

  const handleClick = useCallback(() => {
    if (navigator.share && imageBlob) {
      const file = new File([imageBlob], 'my-sbs-rewind-2023.png', { type: 'image/png' });
      navigator.share({ files: [file] } as ShareData);
    }

    DataLayer.events.shareResults();
  }, [imageBlob]);

  const handleDownloadClick = useCallback(() => {
    if (imageBlob) {
      const link = document.createElement('a');
      link.href = imageDataUrl;
      link.download = 'my-sbs-rewind-2023.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      DataLayer.events.downloadResults();
    }
  }, [imageBlob, imageDataUrl]);

  return (
    <div
      className="flex flex-col lg:flex-row justify-center items-center
        gap-4 xs:gap-10 sm:gap-20 xl:gap-40 2xl:gap-80
        px-0 lg:mx-40"
    >
      <h2 className="lg:hidden text-xl md:text-2xl lg:text-2xl xl:text-4xl lg:text-left slide-up">
        <span className="text-dark-tangerine">Thank you</span>
        {' '}
        for watching with us in 2023!
      </h2>

      <Image
        className="!ml-[0]
          !w-[260px] !h-[260px] sm:!w-[400px] sm:!h-[400px] xl:!w-[490px] xl:!h-[490px]
          border-[1px] border-solid border-black rounded-lg shadow-[0_2px_8px_0_rgba(0,0,0,0.3)]
          slide-in-from-left delay-[900ms]"
        src={imageDataUrl}
        alt="Your achievement"
        width={1000}
        height={1000}
      />

      <div
        className="flex flex-col items-center lg:!items-start justify-center
        mx-2 lg:mx-0 sm:max-w-[500px] min-w-[300px]"
      >
        <h2 className="hidden lg:block text-xl md:text-3xl lg:text-3xl 2xl:text-4xl lg:text-left slide-up">
          <span className="text-dark-tangerine">Thank you</span>
          {' '}
          for watching with us in 2023!
        </h2>
        {
          showShareButton && (
            <button
              type="button"
              className="sbs-button sbs-button-primary mt-0 lg:mt-16 slide-down delay-[600ms]"
              onClick={handleClick}
              tabIndex={active ? 0 : -1}
            >
              Share your results
            </button>
          )
        }
        {
          !showShareButton && (
            <button
              type="button"
              className="sbs-button sbs-button-primary mt-0 lg:mt-16 slide-down delay-[600ms]"
              onClick={handleDownloadClick}
              tabIndex={active ? 0 : -1}
            >
              Download your results
            </button>
          )
        }
      </div>
    </div>
  );
};

export default ThankYou;
